import React from 'react';
import { Modal } from 'antd';
import type {
    PreContractQuoteReview,
    PreContractQuoteReviewItem,
} from '../lib/preContractQuoteReview';

type Props = {
    review: PreContractQuoteReview | null;
    confirming?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

function ReviewSection({
    title,
    items,
}: {
    title: string;
    items: PreContractQuoteReviewItem[];
}) {
    if (!items.length) return null;
    return (
        <section className="pci-quote-review__section">
            <h3>{title}</h3>
            <dl className="pci-quote-review__grid">
                {items.map((item) => (
                    <React.Fragment key={`${title}-${item.label}`}>
                        <dt>{item.label}</dt>
                        <dd>{item.value || '—'}</dd>
                    </React.Fragment>
                ))}
            </dl>
        </section>
    );
}

export const PreContractQuoteReviewModal: React.FC<Props> = ({
    review,
    confirming = false,
    onConfirm,
    onCancel,
}) => (
    <Modal
        open={Boolean(review)}
        title={review ? `Review ${review.product} quote` : 'Review quote'}
        okText="Confirm & continue"
        cancelText="Cancel"
        centered
        destroyOnClose
        confirmLoading={confirming}
        maskClosable={!confirming}
        closable={!confirming}
        onOk={onConfirm}
        onCancel={onCancel}
        className="pci-quote-review"
        rootClassName="pci-quote-review-root"
        zIndex={12600}
    >
        {review ? (
            <div className="pci-quote-review__body">
                <p className="pci-quote-review__intro">
                    Confirm the product, utility, system, and financing selections before partner sync begins.
                </p>
                <ReviewSection
                    title="Product & financing"
                    items={[
                        { label: 'Product', value: review.product },
                        { label: 'Finance partner', value: review.partner },
                        { label: 'Finance method', value: review.financeMethod },
                    ]}
                />
                <ReviewSection
                    title="Utility"
                    items={[
                        { label: 'Utility company', value: review.utilityCompany },
                        { label: 'All-in rate ($/kWh)', value: review.utilityRate },
                    ]}
                />
                <ReviewSection title="System" items={review.systemItems} />
                <ReviewSection title="Rate & pricing" items={review.financeItems} />
            </div>
        ) : null}
    </Modal>
);

export default PreContractQuoteReviewModal;
